'use client';

import * as React from 'react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { useCatalogEditing } from '@/hen-ui/catalog/editing-context';
import { type IconName } from '@/hen-ui/atomics/Icon';
import { useMatchesMedia } from '@/hen-ui/hooks/useIsDesktop';
import { TriggerAriaBridge } from '@/hen-ui/lib/trigger-children';
import { usePortalContainer } from '@/hen-ui/providers/PortalContainerContext';
import {
  Popover as ShadcnPopover,
  PopoverBackdrop as ShadcnPopoverBackdrop,
  PopoverContent as ShadcnPopoverContent,
  PopoverTrigger as ShadcnPopoverTrigger,
} from '@/hen-ui/shadcn/popover';
import { cn, warnOnce } from '@/hen-ui/utils';

import { Modal, ModalContent, ModalTitle, ModalTrigger } from '../Modal/Modal';
import { PopoverButton, type PopoverButtonProps } from './PopoverButton';
import { PopoverButtonAttachedProvider } from './popover-button-context';
import { usePopoverInline } from './popover-inline-context';
import {
  deepActiveElement,
  isFocusLost,
  overlayRootsOf,
  useOverlayHandOff,
  type OverlayRoot,
} from './useOverlayHandOff';
import {
  POPOVER_SHEET_SURFACE_CLASS,
  POPOVER_SHEET_WIDTH_CLASS,
  POPOVER_SIDE_OFFSET,
  POPOVER_TRIGGER_ABOVE_DIM_CLASS,
  popoverCollisionPadding,
  popoverContentVariants,
  popoverSectionVariants,
  type PopoverAlign,
  type PopoverBackdrop,
  type PopoverCollapseBelow,
  type PopoverMobilePanelSide,
  type PopoverShadow,
} from './popover.variants';

const WIDE_VIEWPORT_QUERY: Record<PopoverCollapseBelow, string> = {
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
};

/** Accessible name when the author leaves `title` empty. */
const FALLBACK_TITLE = 'Menu';

const OVERLAY_SELECTOR = '[role="dialog"],[role="alertdialog"]';

export interface PopoverProps {
  /**
   * Controlled open state. Pair with `onOpenChange`. Without a handler it sets
   * the initial state, and on the editor canvas each change opens or closes
   * the panel.
   */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Label of the trigger button the popover draws. Always the trigger's
   * accessible name, also when `triggerLabelHidden` is set. Falls back to
   * `title`, then "Menu".
   */
  triggerLabel?: string;
  /** Leading icon of the trigger. */
  triggerIcon?: IconName;
  /** Leading icon while the panel is open. Without it `triggerIcon` stays. */
  triggerExpandedIcon?: IconName;
  /** Shows only `triggerIcon`, with the label kept for screen readers. */
  triggerLabelHidden?: boolean;
  /**
   * Shows only `triggerIcon` below this screen size and the label with the
   * icon from it up, with the label kept for screen readers. Styled in CSS,
   * so the server render already matches. `triggerLabelHidden` wins.
   */
  triggerLabelHiddenBelow?: PopoverCollapseBelow;
  /** Runs on each click of the trigger, before the panel opens or closes. */
  onTriggerClick?: React.MouseEventHandler<HTMLButtonElement>;
  header?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  /** Accessible name of the panel; also the sheet title on small screens. */
  title?: string;
  /** Edge of the trigger the panel lines up with. */
  align?: PopoverAlign;
  /** Joins the panel to the trigger, which shows as an open tab. */
  attached?: boolean;
  shadow?: PopoverShadow;
  /**
   * Dims the page behind the open panel on wide screens. The Navbar and the
   * trigger stay above the dim, unless the trigger sits in a container that
   * layers itself above the page, such as a card that is set to float or
   * move; the trigger then dims with that container.
   */
  backdrop?: PopoverBackdrop;
  /** Edge the side panel opens from on small screens. */
  mobilePanelSide?: PopoverMobilePanelSide;
  /** Below this screen size the panel opens as a side sheet instead. */
  collapseBelow?: PopoverCollapseBelow;
  /** Compound API: `Popover.Trigger` and `Popover.Content`. */
  children?: React.ReactNode;
  /** `data-*` attributes go on the trigger the popover draws. */
  [dataAttribute: `data-${string}`]: string | undefined;
}

interface PopoverContextValue {
  collapsed: boolean;
  /** Laid out in place inside another overlay; no trigger, no panel. */
  inline: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  align: PopoverAlign;
  attached: boolean;
  shadow: PopoverShadow;
  backdrop: PopoverBackdrop;
  title?: string;
  mobilePanelSide: PopoverMobilePanelSide;
  editing: boolean;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  panelRef: React.MutableRefObject<Element | null>;
  /** Holds the dialog the panel closed for, until that dialog closes. */
  handOffRef: React.MutableRefObject<Element | null>;
  /** Latest `onTriggerClick`; `PopoverTrigger` calls it for either API. */
  onTriggerClickRef: React.MutableRefObject<
    React.MouseEventHandler<HTMLButtonElement> | undefined
  >;
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

function usePopoverContext(part: string): PopoverContextValue {
  const context = React.useContext(PopoverContext);
  if (!context) {
    throw new Error(`Popover.${part} must be rendered inside a Popover.`);
  }
  return context;
}

function viewportCanBeMeasured(): boolean {
  return (
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  );
}

/** Keeps only `data-*` keys, so props from a renderer never reach the DOM. */
function dataAttributes(props: object): Record<string, string> {
  return Object.fromEntries(
    Object.entries(props).filter(
      (entry): entry is [string, string] =>
        entry[0].startsWith('data-') && typeof entry[1] === 'string',
    ),
  );
}

function hasContent(node: React.ReactNode): boolean {
  return node != null && node !== false && node !== '';
}

function closestOverlay(target: EventTarget | null): Element | null {
  const element = target as Element | null;
  return typeof element?.closest === 'function'
    ? element.closest(OVERLAY_SELECTOR)
    : null;
}

/** True when `target` sits in a dialog other than `own`, e.g. a modal opened from the panel. */
function isInOtherOverlay(
  target: EventTarget | null,
  own: Element | null,
): boolean {
  const overlay = closestOverlay(target);
  return overlay != null && overlay !== own && !own?.contains(overlay);
}

/**
 * Radix only returns focus to the trigger after Escape. Return it after an
 * outside click too, unless the visitor already moved focus elsewhere.
 */
function restoreTriggerFocus(
  trigger: HTMLElement | null,
  content: Element | null,
) {
  if (!trigger?.isConnected) return;
  const doc = trigger.ownerDocument;
  const active = deepActiveElement(doc);
  const focusWasLost =
    isFocusLost(doc) || (active != null && !!content?.contains(active));
  if (!focusWasLost) return;
  trigger.focus({ preventScroll: true });
}

/**
 * Whether `node` or an ancestor, across shadow hosts, is `display: none`, so
 * the trigger has no layout box: a copy the page hides at this width.
 */
function hasNoLayoutBox(node: Element | null): boolean {
  if (!node?.isConnected) return false;
  const view = node.ownerDocument.defaultView;
  let current: Element | null = node;
  while (current) {
    if (view?.getComputedStyle(current).display === 'none') return true;
    const root = current.getRootNode();
    current =
      current.parentElement ??
      (root.nodeType === Node.DOCUMENT_FRAGMENT_NODE && 'host' in root
        ? (root as ShadowRoot).host
        : null);
  }
  return false;
}

/** Forwards the panel node and keeps the last one for hand-off checks. */
function usePanelRefs<T extends Element>(
  ref: React.ForwardedRef<T>,
  panelRef: React.MutableRefObject<Element | null>,
) {
  return React.useCallback(
    (node: T | null) => {
      if (node) panelRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref, panelRef],
  );
}

const PopoverRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  triggerLabel,
  triggerIcon,
  triggerExpandedIcon,
  triggerLabelHidden = false,
  triggerLabelHiddenBelow,
  onTriggerClick,
  header,
  body,
  footer,
  title,
  align = 'center',
  attached = false,
  shadow = 'none',
  backdrop = 'none',
  mobilePanelSide = 'right',
  collapseBelow = 'lg',
  children,
  ...rest
}: PopoverProps) => {
  const editing = useCatalogEditing();
  const triggerAttributes = dataAttributes(rest);
  const isWide = useMatchesMedia(
    WIDE_VIEWPORT_QUERY[collapseBelow] ?? WIDE_VIEWPORT_QUERY.lg,
  );
  // The server and the first client render cannot know the screen size, so
  // the panel stays closed until the layout is known.
  const [measured, setMeasured] = React.useState(false);
  React.useEffect(() => setMeasured(true), []);

  const isControlled = open !== undefined && onOpenChange !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    open ?? defaultOpen ?? false,
  );
  const currentOpen = isControlled ? open : uncontrolledOpen;
  // On the editor canvas an authored `open` with no handler is the author's
  // Open toggle: follow each change of the value, while the trigger, Escape,
  // and outside clicks can still close the panel in between.
  const mirrorAuthoredOpen =
    editing && open !== undefined && onOpenChange === undefined;
  React.useEffect(() => {
    if (mirrorAuthoredOpen) setUncontrolledOpen(open);
  }, [mirrorAuthoredOpen, open]);
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const inline = usePopoverInline();
  const collapsed =
    !inline && !editing && measured && viewportCanBeMeasured() && !isWide;
  const triggerRef = React.useRef<HTMLElement | null>(null);
  // A sheet whose trigger is hidden has nothing visible to return focus to;
  // a visible copy of the same actions (the Navbar drawer) shows them instead.
  // `null` until the trigger is checked for this open sheet, so a sheet with
  // a hidden trigger never mounts, not even for one commit.
  const sheetRequested = collapsed && currentOpen;
  const [triggerHidden, setTriggerHidden] = React.useState<boolean | null>(
    null,
  );
  React.useLayoutEffect(() => {
    setTriggerHidden(
      sheetRequested ? hasNoLayoutBox(triggerRef.current) : null,
    );
  }, [sheetRequested]);
  const resolvedOpen =
    !inline &&
    measured &&
    currentOpen &&
    (!collapsed || triggerHidden === false);
  React.useEffect(() => {
    if (triggerHidden) setOpen(false);
  }, [triggerHidden, setOpen]);
  const panelRef = React.useRef<Element | null>(null);
  const onTriggerClickRef = React.useRef(onTriggerClick);
  onTriggerClickRef.current = onTriggerClick;
  const portalContainer = usePortalContainer();

  // In an embed the trigger and the portal container can sit in shadow
  // roots, which a watcher on the document body does not see into.
  const overlayRoots = React.useCallback((): OverlayRoot[] => {
    const roots = [triggerRef.current, portalContainer]
      .filter((node): node is HTMLElement => node != null)
      .flatMap(overlayRootsOf);
    if (roots.length === 0 && typeof document !== 'undefined') {
      roots.push(document);
    }
    return Array.from(new Set(roots));
  }, [portalContainer]);
  const close = React.useCallback(() => setOpen(false), [setOpen]);
  const handOffRef = useOverlayHandOff({
    open: resolvedOpen,
    enabled: !editing,
    panelRef,
    triggerRef,
    roots: overlayRoots,
    close,
  });

  const context = React.useMemo<PopoverContextValue>(
    () => ({
      collapsed,
      inline,
      open: resolvedOpen,
      setOpen,
      align,
      attached,
      shadow,
      backdrop,
      title,
      mobilePanelSide,
      editing,
      triggerRef,
      panelRef,
      handOffRef,
      onTriggerClickRef,
    }),
    [
      collapsed,
      inline,
      resolvedOpen,
      setOpen,
      align,
      attached,
      shadow,
      backdrop,
      title,
      mobilePanelSide,
      editing,
      handOffRef,
    ],
  );

  const isSlotted =
    triggerLabel !== undefined ||
    header !== undefined ||
    body !== undefined ||
    footer !== undefined;

  const content = isSlotted ? (
    <>
      <PopoverTrigger>
        <PopoverButton
          {...triggerAttributes}
          icon={triggerIcon}
          expandedIcon={triggerExpandedIcon}
          labelHidden={triggerLabelHidden}
          labelHiddenBelow={triggerLabelHiddenBelow}
        >
          {triggerLabel?.trim() || title?.trim() || FALLBACK_TITLE}
        </PopoverButton>
      </PopoverTrigger>
      <PopoverContent>
        {hasContent(header) ? <PopoverHeader>{header}</PopoverHeader> : null}
        {hasContent(body) ? <PopoverBody>{body}</PopoverBody> : null}
        {hasContent(footer) ? <PopoverFooter>{footer}</PopoverFooter> : null}
      </PopoverContent>
    </>
  ) : (
    children
  );

  return (
    <PopoverContext.Provider value={context}>
      {inline ? (
        content
      ) : collapsed ? (
        <Modal open={resolvedOpen} onOpenChange={setOpen}>
          {content}
        </Modal>
      ) : (
        <ShadcnPopover open={resolvedOpen} onOpenChange={setOpen}>
          {content}
        </ShadcnPopover>
      )}
    </PopoverContext.Provider>
  );
};

PopoverRoot.displayName = 'Popover';

export interface PopoverTriggerProps {
  /** A single `PopoverButton`. Any other child is not rendered. */
  children?: React.ReactNode;
  className?: string;
}

function isPopoverButton(
  node: React.ReactNode,
): node is React.ReactElement<PopoverButtonProps> {
  return React.isValidElement(node) && node.type === PopoverButton;
}

export const PopoverTrigger = ({
  children,
  className,
}: PopoverTriggerProps) => {
  const {
    collapsed,
    inline,
    open,
    attached,
    backdrop,
    editing,
    triggerRef,
    onTriggerClickRef,
  } = usePopoverContext('Trigger');
  const first = React.Children.toArray(children).find(
    (child) => child != null && typeof child !== 'boolean',
  );
  if (first == null || inline) return null;
  if (!isPopoverButton(first)) {
    warnOnce(
      'Popover/trigger-not-popover-button',
      'Popover.Trigger renders only a PopoverButton child; the trigger was skipped.',
    );
    return null;
  }

  const ownClick = first.props.onClick;
  // The button's own handler first, then `onTriggerClick`, both before the
  // panel toggles.
  const button = React.cloneElement(first, {
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      ownClick?.(event);
      onTriggerClickRef.current?.(event);
    },
  });

  const aboveDim = open && backdrop === 'dim' && !collapsed && !editing;

  // On the bridge, not the outer trigger: the dialog trigger drops refs.
  const bridged = (
    <TriggerAriaBridge
      ref={triggerRef as React.Ref<HTMLElement>}
      asChild
      className={cn(aboveDim && POPOVER_TRIGGER_ABOVE_DIM_CLASS, className)}
    >
      {button}
    </TriggerAriaBridge>
  );

  return (
    <PopoverButtonAttachedProvider value={attached && !collapsed}>
      {collapsed ? (
        <ModalTrigger asChild>{bridged}</ModalTrigger>
      ) : (
        <ShadcnPopoverTrigger asChild>{bridged}</ShadcnPopoverTrigger>
      )}
    </PopoverButtonAttachedProvider>
  );
};

PopoverTrigger.displayName = 'PopoverTrigger';

export interface PopoverContentProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ShadcnPopoverContent>,
    'align' | 'side' | 'sideOffset' | 'asChild'
  > {}

/** Props that only place the desktop panel; the side sheet has no use for them. */
type PopoverPositioningProp =
  | 'avoidCollisions'
  | 'collisionPadding'
  | 'collisionBoundary'
  | 'sticky'
  | 'hideWhenDetached'
  | 'arrowPadding'
  | 'updatePositionStrategy';

type PopoverSheetContentProps = Omit<
  PopoverContentProps,
  PopoverPositioningProp
>;

function withoutPositioning(
  props: Omit<PopoverContentProps, 'className' | 'children'>,
): Omit<PopoverSheetContentProps, 'className' | 'children'> {
  const {
    avoidCollisions: _avoidCollisions,
    collisionPadding: _collisionPadding,
    collisionBoundary: _collisionBoundary,
    sticky: _sticky,
    hideWhenDetached: _hideWhenDetached,
    arrowPadding: _arrowPadding,
    updatePositionStrategy: _updatePositionStrategy,
    ...rest
  } = props;
  return rest;
}

/** Keeps `id`, `aria-*`, and `data-*`; the in-place group takes no other content props. */
function inlineAttributes(props: object): React.HTMLAttributes<HTMLDivElement> {
  return Object.fromEntries(
    Object.entries(props).filter(
      ([key]) =>
        key === 'id' || key.startsWith('aria-') || key.startsWith('data-'),
    ),
  );
}

export const PopoverContent = React.forwardRef<
  HTMLDivElement,
  PopoverContentProps
>(
  (
    {
      className,
      children,
      onInteractOutside,
      onCloseAutoFocus,
      collisionPadding,
      ...props
    },
    ref,
  ) => {
    const context = usePopoverContext('Content');
    const accessibleTitle = context.title || FALLBACK_TITLE;
    const setRefs = usePanelRefs(ref, context.panelRef);

    if (context.inline) {
      return (
        <div
          ref={ref}
          role='group'
          aria-label={accessibleTitle}
          {...inlineAttributes(props)}
          className={cn('flex flex-col gap-sm', className)}
          data-hen-id={COMPONENT_IDS.Popover}
        >
          {children}
        </div>
      );
    }

    if (context.collapsed) {
      return (
        <PopoverSheetContent
          ref={ref}
          className={className}
          onInteractOutside={onInteractOutside}
          onCloseAutoFocus={onCloseAutoFocus}
          {...withoutPositioning(props)}
        >
          {children}
        </PopoverSheetContent>
      );
    }

    const { align, attached, shadow, editing, triggerRef, handOffRef } =
      context;
    return (
      <>
        {context.backdrop === 'dim' && !editing ? (
          <ShadcnPopoverBackdrop open={context.open} />
        ) : null}
        <ShadcnPopoverContent
          ref={setRefs}
          align={align}
          sideOffset={
            attached
              ? POPOVER_SIDE_OFFSET.attached
              : POPOVER_SIDE_OFFSET.detached
          }
          // An attached panel stays joined to its trigger instead of shifting.
          avoidCollisions={!attached}
          collisionPadding={popoverCollisionPadding(collisionPadding)}
          aria-label={accessibleTitle}
          data-attached={attached ? '' : undefined}
          className={cn(
            popoverContentVariants({ align, attached, shadow }),
            className,
          )}
          onInteractOutside={(event) => {
            onInteractOutside?.(event);
            // The editor canvas keeps the panel open while authors drop in blocks.
            if (editing) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event);
            if (event.defaultPrevented) return;
            event.preventDefault();
            // Focus belongs to the dialog the panel closed for.
            if (handOffRef.current) return;
            restoreTriggerFocus(
              triggerRef.current,
              event.currentTarget as Element | null,
            );
          }}
          {...props}
          data-hen-id={COMPONENT_IDS.Popover}
        >
          {children}
        </ShadcnPopoverContent>
      </>
    );
  },
);

PopoverContent.displayName = 'PopoverContent';

const PopoverSheetContent = React.forwardRef<
  HTMLDivElement,
  PopoverSheetContentProps
>(
  (
    { className, children, onFocusOutside, onCloseAutoFocus, ...props },
    ref,
  ) => {
    const { title, mobilePanelSide, setOpen, panelRef, handOffRef } =
      usePopoverContext('Content');
    const ownRef = React.useRef<HTMLDivElement | null>(null);
    const setPanelRefs = usePanelRefs(ref, panelRef);
    // The dialog content does not forward a ref, so find it from inside.
    const setRefs = React.useCallback(
      (node: HTMLDivElement | null) => {
        const dialog = node?.closest<HTMLDivElement>(OVERLAY_SELECTOR) ?? null;
        ownRef.current = dialog;
        setPanelRefs(dialog);
      },
      [setPanelRefs],
    );

    return (
      <ModalContent
        aria-describedby={undefined}
        {...props}
        variant='sheet'
        panelSide={mobilePanelSide}
        closePlacement='outside'
        headerTitle={title || undefined}
        bodyClassName='p-modal-sheet-padding'
        className={cn(
          POPOVER_SHEET_WIDTH_CLASS,
          POPOVER_SHEET_SURFACE_CLASS,
          className,
        )}
        // Another dialog opened from the panel takes over; never stack two.
        onFocusOutside={(event) => {
          onFocusOutside?.(event);
          if (event.defaultPrevented) return;
          if (
            isInOtherOverlay(event.detail.originalEvent.target, ownRef.current)
          ) {
            setOpen(false);
          }
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          if (event.defaultPrevented) return;
          const active = ownRef.current?.ownerDocument.activeElement ?? null;
          if (handOffRef.current || isInOtherOverlay(active, ownRef.current)) {
            event.preventDefault();
          }
        }}
        data-hen-id={COMPONENT_IDS.Popover}
      >
        {title ? null : (
          <ModalTitle className='sr-only'>{FALLBACK_TITLE}</ModalTitle>
        )}
        <div ref={setRefs} className='flex flex-col gap-sm'>
          {children}
        </div>
      </ModalContent>
    );
  },
);

PopoverSheetContent.displayName = 'PopoverSheetContent';

export interface PopoverSectionProps
  extends React.ComponentPropsWithoutRef<'div'> {}

function createSection(slot: string, displayName: string) {
  const Section = React.forwardRef<HTMLDivElement, PopoverSectionProps>(
    ({ className, ...props }, ref) => (
      <div
        ref={ref}
        data-slot={slot}
        className={cn(popoverSectionVariants(), className)}
        {...props}
      />
    ),
  );
  Section.displayName = displayName;
  return Section;
}

export const PopoverHeader = createSection('popover-header', 'PopoverHeader');
export const PopoverBody = createSection('popover-body', 'PopoverBody');
export const PopoverFooter = createSection('popover-footer', 'PopoverFooter');

export const Popover = Object.assign(PopoverRoot, {
  Trigger: PopoverTrigger,
  Content: PopoverContent,
  Header: PopoverHeader,
  Body: PopoverBody,
  Footer: PopoverFooter,
});
